import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import BackIcon from '../../images/BackIcon';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import {
  createProductModifier,
  getOneProductModifier,
  updateProductModifier,
  CreateProductModifierPayload,
  ProductModifierDetail,
} from '../../services/productModifiers.service';
import { getListProduct } from '../../services/items.services';
import { DropdownSearch } from '../../components/DropdownSearch';
import CheckIcon from '../../images/CheckIcon';
import PlusIcon from '../../images/PlusIcon';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import SearchIcon from '../../images/SearchIcon';
import WhiteCheckIcon from '../../images/WhiteCheckIcon';
import { useResponsive } from '../../hook/useResponsive';
import { useInfiniteScroll } from '../../hook/useInfiniteScroll';
import { useFormKeyboard } from '../../hook/useFormKeyboard';
import { validateNoSpecialChars } from '../../validation/ValidationInput';
import CreateProductModifierMobile from './CreateProductModifierMobile';

const MODIFIER_TYPES = [
  { id: 1, name: 'Extras', value: 1 },
  { id: 2, name: 'Services', value: 2 },
  { id: 3, name: 'Restrictions', value: 3 },
];

interface AnswerRow {
  id?: string;
  answer_text: string;
  product_id: string | null;
  color_code: string | null;
  display_order: number;
  max_answer?: number;
  is_active?: boolean;
  date?: Date | string;
}

export interface ProductOption {
  id: string;
  productName: string;
  sku: string;
  productGroup: { groupName: string };
  updatedAt: Date;
  isActive: boolean;
  isAttributes: true | false;
}

interface ProductResponse {
  data: {
    productWithCompany: ProductOption[];
    count: number;
  };
}

const STEP_LABELS = {
  step1: 'Modifier',
  step2: 'Available options',
  step3: 'Applicable products',
} as const;

const PAGE_SIZE = 50;
const STEP3_SEARCH_DEBOUNCE_MS = 300;
// Every limited field sits on step 1, so a save error jumps back there.
const MODIFIER_FIELD_STEPS: Record<string, number> = { name: 1, modifier_type: 1, min_answer: 1, max_answer: 1, display_order: 1 };

// Space on a div switch or checkbox clicks it once, as on a native checkbox; Enter is left to the form.
const clickOnSpace = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey) return;
  e.preventDefault();
  if (!e.repeat) e.currentTarget.click();
};

const CreateProductModifier: React.FC = () => {
  const preLanguage = localStorage.getItem('language');
  const path = window.location.pathname;
  const id: string = path.split('/').pop() || '';
  const companyId = localStorage.getItem('companyId') || '';
  const { isMobile } = useResponsive();

  const typeRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(1);
  const kb = useFormKeyboard({ step, autoFocus: id === 'new' && !isMobile });
  // Step 2's add row: Enter there presses Add, not Next.
  const rowKb = useFormKeyboard();
  const [language, setLanguage] = useState<any>(null);
  const limits = useFieldLimits(inventoryFormLimits('product-modifier'));
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [modifierTypeLabel, setModifierTypeLabel] = useState('');

  const [formValues, setFormValues] = useState({
    name: '',
    modifier_type: 1 as number,
    min_answer: 0,
    max_answer: 1,
    is_all: false,
    is_device: true,
    is_web: true,
    display_order: 0,
    is_active: true,
  });

  const [products, setProducts] = useState<{ product_id: string }[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [productOptionsPage, setProductOptionsPage] = useState(1);
  const [productOptionsHasMore, setProductOptionsHasMore] = useState(true);
  const [productOptionsLoading, setProductOptionsLoading] = useState(false);
  const [productOptionsSearch, setProductOptionsSearch] = useState('');
  const [listProducts, setListProducts] = useState<ProductOption[]>([]);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initalFilter = { searchProduct: '', pageProduct: 1, searchValue: '' };
  const [filter, setFilter] = useState<any>(initalFilter);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const hasLoadedPageRef = useRef<Set<number>>(new Set());
  const [hasMore, setHasMore] = useState(true);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const scrollProductRef = useRef<HTMLDivElement>(null);

  const [addForm, setAddForm] = useState<{
    answer_text: string;
    product_id: string | null;
    display_order: number;
    max_answer: number;
  }>({ answer_text: '', product_id: null, display_order: 0, max_answer: 1 });
  const [editingAnswerIndex, setEditingAnswerIndex] = useState<number | null>(null);

  const productDropdownRef = useRef<HTMLDivElement>(null);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productDropdownLabel, setProductDropdownLabel] = useState('');

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, []);

  /** What the screen showed right after loading; the mobile Back / Cancel guard compares against it. */
  const loadedSnapshotRef = useRef<string>(JSON.stringify({ formValues, answers, products }));
  const snapshotPendingRef = useRef(id !== 'new');
  useEffect(() => {
    if (!snapshotPendingRef.current) return;
    if (formValues.name === '' && answers.length === 0 && products.length === 0) return;
    snapshotPendingRef.current = false;
    loadedSnapshotRef.current = JSON.stringify({ formValues, answers, products });
  }, [formValues, answers, products]);

  useEffect(() => {
    const t = MODIFIER_TYPES.find((x) => x.value === formValues.modifier_type);
    setModifierTypeLabel(t ? t.name : '');
  }, [formValues.modifier_type]);

  useEffect(() => {
    if (id !== 'new') {
      webLoadingTrue();
      getOneProductModifier(id)
        .then((res: { data?: ProductModifierDetail }) => {
          const d = res?.data;
          if (!d?.modifier) return;
          const m = d.modifier;
          setFormValues({
            name: m.name,
            modifier_type: m.modifier_type,
            min_answer: m.min_answer,
            max_answer: m.max_answer,
            is_all: m.is_all,
            is_device: m.is_device,
            is_web: m.is_web,
            display_order: m.display_order,
            is_active: m.is_active,
          });
          setProducts((d.products || []).map((p) => ({ product_id: p.product_id })));
          setAnswers(
            (d.answers || []).map((a, i) => ({
              id: a.id,
              answer_text: a.answer_text,
              product_id: a.product_id ?? null,
              color_code: a.color_code ?? null,
              display_order: a.display_order ?? i,
              max_answer: (a as any).max_answer ?? 1,
              is_active: true,
              date: (a as any).date ?? new Date(),
            }))
          );
        })
        .catch((err) => {
          console.error(err);
          // `language` state is still null in this mount-time closure; read the host map directly.
          const lang = window.languageContext?.language;
          showApiError(err, { language: lang, titleFallback: lang?.['Failed to load product modifier'] ?? 'Failed to load product modifier' });
        })
        .finally(() => webLoadingFalse());
    }
  }, [id]);

  const loadProductOptions = useCallback(
    async (search = '', page = 1) => {
      if (formValues.modifier_type !== 1) return;
      setProductOptionsLoading(true);
      try {
        const r: any = await getListProduct(page, PAGE_SIZE, search, companyId);
        const arr: ProductOption[] = r?.data?.productWithCompany ?? [];
        setProductOptions((prev) => {
          if (page === 1) return arr;
          const merged = [...prev, ...arr];
          const seen = new Set<string>();
          return merged.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
        });
        setProductOptionsPage(page);
        setProductOptionsHasMore(arr.length >= PAGE_SIZE);
      } catch (_err) {
        if (page === 1) setProductOptions([]);
        setProductOptionsHasMore(false);
      } finally {
        setProductOptionsLoading(false);
      }
    },
    [companyId, formValues.modifier_type]
  );

  useEffect(() => {
    if (formValues.modifier_type !== 1) {
      setProductOptions([]);
      setProductOptionsPage(1);
      setProductOptionsHasMore(true);
      setProductOptionsSearch('');
      return;
    }
    setProductOptionsSearch('');
    setProductOptionsPage(1);
    setProductOptionsHasMore(true);
    loadProductOptions('', 1);
  }, [companyId, formValues.modifier_type, loadProductOptions]);

  const productsRef = useRef<{ product_id: string }[]>([]);
  productsRef.current = products;
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const fetchAndAppendProductList = async (
    filterParam: any,
    isAppend: boolean = true
  ) => {
    try {
      const response: ProductResponse = await getListProduct(
        filterParam.pageProduct,
        PAGE_SIZE,
        filterParam.searchProduct,
        companyId
      );

      const fetchedProducts = response.data.productWithCompany;
      const productSelectIds = new Set(productsRef.current.map((p) => p.product_id));
      const updatedProducts = fetchedProducts.map((value) => ({
        ...value,
        isAttributes: productSelectIds.has(value.id),
      }));

      if (isAppend) {
        setListProducts((prev) => [...prev, ...updatedProducts]);
      } else {
        setListProducts(updatedProducts);
      }

      setFilter((prev: any) => ({
        ...prev,
        pageProduct: filterParam.pageProduct + 1,
        searchProduct: filterParam.searchProduct,
      }));

      if (fetchedProducts.length < PAGE_SIZE) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (err) {
      console.error('Error fetching product list:', err);
      hasLoadedPageRef.current.delete(filterParam.pageProduct);
    } finally {
      setLoadingProduct(false);
    }
  };

  /** Mobile has no steps: the products list is live whenever the type is Extras. */
  const productsListActive = isMobile || step === 3;

  const loadStep3ProductList = (searchValueRaw: string) => {
    if (!productsListActive || formValues.modifier_type !== 1) return;
    const kw = searchValueRaw.trim();
    hasLoadedPageRef.current.clear();
    setHasMore(true);
    setFilter((prev: any) => ({
      ...prev,
      searchProduct: kw,
      pageProduct: 1,
      searchValue: searchValueRaw,
    }));
    setLoadingProduct(true);
    void fetchAndAppendProductList({ searchProduct: kw, pageProduct: 1 }, false);
  };

  useDebounceEffect(
    () => {
      if (!productsListActive || formValues.modifier_type !== 1) return;
      loadStep3ProductList(filter.searchValue ?? '');
    },
    [filter.searchValue, productsListActive, formValues.modifier_type, id],
    STEP3_SEARCH_DEBOUNCE_MS
  );

  const fetchRef = useRef(fetchAndAppendProductList);
  fetchRef.current = fetchAndAppendProductList;

  const onLoadMoreProduct = useCallback(async () => {
    const currentFilter = filterRef.current;
    if (hasLoadedPageRef.current.has(currentFilter.pageProduct)) {
      return;
    }
    hasLoadedPageRef.current.add(currentFilter.pageProduct);
    setLoadingProduct(true);
    await fetchRef.current(currentFilter, true);
  }, []);

  const { setLoaderRef } = useInfiniteScroll({
    hasMore,
    loading: loadingProduct,
    onLoadMore: onLoadMoreProduct,
    scrollRootRef: scrollProductRef,
  });

  useEffect(() => {
    const selectedIds = new Set(products.map((x) => x.product_id));
    setListProducts((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((p) => ({ ...p, isAttributes: selectedIds.has(p.id) }));
    });
  }, [products]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const val =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : type === 'number' ? Number(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleTypeSelect = (_name: string, value: number) => {
    setFormValues((prev) => ({ ...prev, modifier_type: value }));
    setIsTypeDropdownOpen(false);
  };

  const handleStep1Next = () => {
    const next: Record<string, string> = validateAgainstLimits(formValues, limits, language, Object.keys(MODIFIER_FIELD_STEPS));
    if (!formValues.name?.trim()) next.name = language?.['Name is required'] ?? 'Name is required';
    if (formValues.modifier_type < 1 || formValues.modifier_type > 3) {
      next.modifier_type = language?.['Type is required'] ?? 'Type is required';
    }
    if (formValues.min_answer < 0) next.min_answer = language?.['Min answers required'] ?? 'Min answers required';
    if (formValues.max_answer < 0) next.max_answer = language?.['Max answers required'] ?? 'Max answers required';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStep(2);
  };

  const handleSelectProduct = (value: ProductOption) => {
    const updatedProducts = [...listProducts];
    const index = updatedProducts.findIndex((p) => p.id === value.id);
    const selectedProduct = updatedProducts[index];
    selectedProduct.isAttributes = !selectedProduct.isAttributes;

    const exists = products.some((p) => p.product_id === selectedProduct.id);
    const updatedProductIds = exists
      ? products.filter((p) => p.product_id !== selectedProduct.id)
      : [...products, { product_id: selectedProduct.id }];

    setListProducts(updatedProducts);
    setProducts(updatedProductIds);
  };

  const handleSubmitSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    event.stopPropagation();
    loadStep3ProductList(filter.searchValue ?? '');
  };

  const handleStep2ProductSearch = useCallback(
    async (value: string) => {
      setProductOptionsSearch(value);
      setProductOptionsHasMore(true);
      await loadProductOptions(value, 1);
    },
    [loadProductOptions]
  );

  const handleStep2ProductLoadMore = useCallback(async () => {
    if (productOptionsLoading || !productOptionsHasMore) return;
    await loadProductOptions(productOptionsSearch, productOptionsPage + 1);
  }, [loadProductOptions, productOptionsHasMore, productOptionsLoading, productOptionsPage, productOptionsSearch]);

  const addAnswerFromForm = () => {
    const isExtras = formValues.modifier_type === 1;
    if (isExtras) {
      if (!addForm.product_id) {
        setErrors((e) => ({ ...e, step2: language?.['Answer is required'] ?? 'Answer is required' }));
        return;
      }
    } else {
      if (!addForm.answer_text?.trim()) {
        setErrors((e) => ({ ...e, step2: language?.['Answer is required'] ?? 'Answer is required' }));
        return;
      }
    }
    setErrors((e) => ({ ...e, step2: '' }));

    if (editingAnswerIndex !== null) {
      setAnswers((prev) => {
        const next = [...prev];
        const product = isExtras ? productOptions.find((p) => p.id === addForm.product_id!) : null;
        const newRow: AnswerRow = isExtras
          ? {
              answer_text: product?.productName || '',
              product_id: addForm.product_id,
              color_code: null,
              display_order: addForm.display_order,
              max_answer: addForm.max_answer,
              date: next[editingAnswerIndex].date ?? new Date(),
            }
          : {
              answer_text: addForm.answer_text.trim(),
              product_id: null,
              color_code: null,
              display_order: addForm.display_order,
              date: next[editingAnswerIndex].date ?? new Date(),
            };
        next[editingAnswerIndex] = { ...next[editingAnswerIndex], ...newRow };
        return next;
      });
      setEditingAnswerIndex(null);
    } else {
      const product = isExtras ? productOptions.find((p) => p.id === addForm.product_id!) : null;
      const newRow: AnswerRow = isExtras
        ? {
            answer_text: product?.productName || '',
            product_id: addForm.product_id,
            color_code: null,
            display_order: addForm.display_order,
            max_answer: addForm.max_answer,
            date: new Date(),
            is_active: true,
          }
        : {
            answer_text: addForm.answer_text.trim(),
            product_id: null,
            color_code: null,
            display_order: addForm.display_order,
            date: new Date(),
            is_active: true,
          };
      setAnswers((prev) => [...prev, newRow]);
    }
    setAddForm({ answer_text: '', product_id: null, display_order: answers.length, max_answer: 1 });
    setProductDropdownLabel('');
  };

  const editAnswer = (index: number) => {
    const a = answers[index];
    setAddForm({
      answer_text: a.answer_text,
      product_id: a.product_id,
      display_order: a.display_order,
      max_answer: a.max_answer ?? 1,
    });
    if (a.product_id) {
      const p = productOptions.find((x) => x.id === a.product_id);
      setProductDropdownLabel(p ? (p.sku ? `${p.sku} - ${p.productName}` : p.productName) : a.answer_text);
    } else {
      setProductDropdownLabel('');
    }
    setEditingAnswerIndex(index);
  };

  const removeAnswer = (index: number) => {
    setAnswers((prev) => prev.filter((_, i) => i !== index));
    if (editingAnswerIndex === index) {
      setEditingAnswerIndex(null);
      setAddForm({ answer_text: '', product_id: null, display_order: 0, max_answer: 1 });
    } else if (editingAnswerIndex !== null && editingAnswerIndex > index) {
      setEditingAnswerIndex(editingAnswerIndex - 1);
    }
  };

  const toggleAnswerActive = (index: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], is_active: !next[index].is_active };
      return next;
    });
  };

  const handleStep2Next = () => {
    setStep(3);
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const payload: CreateProductModifierPayload = {
      name: formValues.name.trim(),
      modifier_type: formValues.modifier_type,
      min_answer: formValues.min_answer,
      max_answer: formValues.max_answer,
      is_all: formValues.is_all,
      is_device: formValues.is_device,
      is_web: formValues.is_web,
      display_order: formValues.display_order,
      is_active: formValues.is_active,
      answers:
        formValues.modifier_type === 1
          ? answers.map((a) => ({
              answer_text: (productOptions.find((p) => p.id === a.product_id)?.productName || a.answer_text || 'Option').trim(),
              product_id: a.product_id || undefined,
              color_code: a.color_code || undefined,
              display_order: a.display_order,
              max_answer: a.max_answer ?? 1,
            }))
          : answers.map((a) => ({
              answer_text: a.answer_text.trim(),
              product_id: a.product_id || undefined,
              color_code: a.color_code || undefined,
              display_order: a.display_order,
            })),
    };
    if (formValues.modifier_type === 1 && products.length > 0) {
      payload.products = products;
    }
    const limitErrors = validateAgainstLimits(formValues, limits, language, Object.keys(MODIFIER_FIELD_STEPS));
    if (Object.keys(limitErrors).length > 0) {
      setErrors(limitErrors);
      setStep(1);
      return;
    }

    webLoadingTrue();
    try {
      if (id === 'new') {
        await createProductModifier(payload);
        showToast({ message: 'Product modifier created', type: 'success' });
      } else {
        await updateProductModifier(id, payload);
        showToast({ message: language?.['Product modifier updated'] ?? 'Product modifier updated', type: 'success' });
      }
      webNavigate('/' + preLanguage + '/productModifiers');
    } catch (err: any) {
      const title = id === 'new' ? 'Failed to create product modifier' : 'Failed to update product modifier';
      showApiError(err, { language, titleFallback: language?.[title] ?? title, setFieldErrors: setErrors, fieldSteps: MODIFIER_FIELD_STEPS, onStep: setStep });
    } finally {
      webLoadingFalse();
    }
  };

  const formatDate = (dateInput?: any) => {
    const date = dateInput ? new Date(dateInput) : new Date();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  /** Step 3 → 2 → 1; Step 1 → danh sách (đồng bộ header + footer Cancel/Back). */
  const handleBackNavigation = () => {
    if (step === 1) {
      webNavigate('/' + preLanguage + '/productModifiers');
    } else if (step === 2) {
      setStep(1);
    } else {
      setStep(2);
    }
  };

  if (isMobile) {
    return (
      <CreateProductModifierMobile
        language={language}
        isNew={id === 'new'}
        formValues={formValues}
        setFormValues={setFormValues}
        answers={answers}
        setAnswers={setAnswers}
        products={products}
        listProducts={listProducts}
        onToggleProduct={handleSelectProduct}
        productSearchValue={filter.searchValue ?? ''}
        onProductSearchChange={(value) => setFilter((prev: any) => ({ ...prev, searchValue: value }))}
        loadingProducts={loadingProduct}
        hasMoreProducts={hasMore}
        onLoadMoreProducts={onLoadMoreProduct}
        productOptions={productOptions}
        productOptionsLoading={productOptionsLoading}
        productOptionsHasMore={productOptionsHasMore}
        onProductOptionsSearch={handleStep2ProductSearch}
        onProductOptionsLoadMore={handleStep2ProductLoadMore}
        onSave={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
        onLeave={() => webNavigate('/' + preLanguage + '/productModifiers')}
        dirty={JSON.stringify({ formValues, answers, products }) !== loadedSnapshotRef.current}
        errors={errors}
      />
    );
  }

  return (
    <div {...kb.scopeProps}>
    <div className="bg-white h-full">
      {/* Back */}
      <button type="button" className="flex items-center cursor-pointer" {...kb.stepProps(handleBackNavigation)}>
        <div className="w-6 h-6 flex items-center justify-center">
          <BackIcon />
        </div>
        <p
          style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
          className="font-inter"
        >
          {language?.['Back'] ?? 'Back'}
        </p>
      </button>

      {/* Title */}
      <p
        style={{ margin: '20px 0px', fontSize: '20px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
        className="font-inter"
      >
        {id === 'new'
          ? (language?.['Add modifier'] ?? 'Add modifier')
          : (language?.['Edit modifier'] ?? 'Edit modifier')}
      </p>

      {/* Stepper */}
      <div className="flex items-start lg:items-center" style={{ gap: isMobile ? '10px' : '20px', marginTop: isMobile ? '2px' : '0' }}>
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            style={{
              width: isMobile ? 'calc(33.333% - 7px)' : 'calc(33% - 10px)',
            }}
          >
            <div
              style={{
                minHeight: '4px',
                width: "100%",
                backgroundColor: step >= s ? '#0F7679' : '#DBE4E4',
                borderRadius: '20px',
              }}
            />
            <div style={{ marginTop: isMobile ? '6px' : '12px' }}>
              <div className="flex items-center" style={{ gap: '8px' }}>
                {step > s && (
                  <div className="flex items-center justify-center" style={{ width: '20px', height: '20px' }}>
                    <CheckIcon />
                  </div>
                )}
                <p
                  className="text-[14px] font-inter leading-[20px] font-medium"
                  style={{ color: step >= s ? '#0F7679' : '#6A7878' }}
                >
                  {language?.['Step ' + s] ?? `Step ${s}`}
                </p>
              </div>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#222525' }}
              >
                {s === 1 && (language?.[STEP_LABELS.step1] ?? STEP_LABELS.step1)}
                {s === 2 && (language?.[STEP_LABELS.step2] ?? STEP_LABELS.step2)}
                {s === 3 && (language?.[STEP_LABELS.step3] ?? STEP_LABELS.step3)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div
        className="scroll-container"
        style={{
          height: isMobile ? 'calc(100% - 264px)' : 'calc(100% - 219px)',
          paddingBottom: isMobile ? '16px' : '0px',
        }}
      >
        {/* ===== STEP 1 ===== */}
        {step === 1 && (
          <div className="rounded-md bg-white flex items-start" style={{ marginTop: '24px', flexWrap: 'wrap', gap: 20 }}>
            {/* Modifier name */}
            <div style={{ width: isMobile ? '100%' : 'calc(33% - 10px)' }}>
              <div className="space-y-2">
                <div className="flex items-center">
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                    {language?.['Modifier name'] ?? 'Modifier name'}
                  </p>
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                </div>
                <div className="w-full">
                  <input
                    type="text"
                    name="name"
                    {...limitProps(limits, 'name')}
                    value={formValues.name}
                    onChange={handleChange}
                    onKeyDown={validateNoSpecialChars}
                    placeholder={language?.['e.g. Choose milk'] ?? 'e.g. Choose milk'}
                    className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                  <FieldCounter value={formValues.name} max={limits.name?.maxLength} />
                </div>
                {errors.name && <p className="text-[12px] font-inter" style={{ color: '#BA0E13' }}>{errors.name}</p>}
              </div>
            </div>

            {/* Modifier type */}
            <div style={{ width: isMobile ? '100%' : 'calc(33% - 10px)' }}>
              <div className="space-y-2">
                <div className="flex items-center">
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                    {language?.['Modifier type'] ?? 'Modifier type'}
                  </p>
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                </div>
                <DropdownSearch<{ name: string; value: number }>
                  refKey="type"
                  refs={{ type: typeRef }}
                  openStates={{ type: isTypeDropdownOpen }}
                  open={(key) => key === 'type' && setIsTypeDropdownOpen(true)}
                  close={(key) => key === 'type' && setIsTypeDropdownOpen(false)}
                  data={MODIFIER_TYPES}
                  labelExtractor={(item) => item.name}
                  value={modifierTypeLabel}
                  setValue={() => {}}
                  onSelect={(item) => handleTypeSelect(item.name, item.value)}
                  borderNone={false}
                />
                {errors.modifier_type && <p className="text-[12px] font-inter" style={{ color: '#BA0E13' }}>{errors.modifier_type}</p>}
              </div>
            </div>

            {/* Display order */}
            <div style={{ width: isMobile ? '100%' : 'calc(33% - 10px)' }}>
              <div className="space-y-2">
                <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                  {language?.['Display order'] ?? 'Display order'}
                </p>
                <div className="w-full">
                  <input
                    type="number"
                    name="display_order"
                    min={0}
                    {...limitProps(limits, 'display_order', 'number')}
                    value={formValues.display_order}
                    onChange={handleChange}
                    className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                </div>
                {errors.display_order && <p className="text-[12px] font-inter" style={{ color: '#BA0E13' }}>{errors.display_order}</p>}
              </div>
            </div>

            {/* Minimum answers */}
            <div style={{ width: isMobile ? '100%' : 'calc(49% - 10px)' }}>
              <div className="space-y-2">
                <div className="flex items-center">
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                    {language?.['Minimum answers'] ?? 'Minimum answers'}
                  </p>
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                </div>
                <div className="w-full">
                  <input
                    type="number"
                    name="min_answer"
                    min={0}
                    {...limitProps(limits, 'min_answer', 'number')}
                    value={formValues.min_answer}
                    onChange={handleChange}
                    className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                </div>
                {errors.min_answer && <p className="text-[12px] font-inter" style={{ color: '#BA0E13' }}>{errors.min_answer}</p>}
              </div>
            </div>

            {/* Maximum answers */}
            <div style={{ width: isMobile ? '100%' : 'calc(49% - 10px)' }}>
              <div className="space-y-2">
                <div className="flex items-center">
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                    {language?.['Maximum answers'] ?? 'Maximum answers'}
                  </p>
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                </div>
                <div className="w-full">
                  <input
                    type="number"
                    name="max_answer"
                    min={0}
                    {...limitProps(limits, 'max_answer', 'number')}
                    value={formValues.max_answer}
                    onChange={handleChange}
                    className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                </div>
                {errors.max_answer && <p className="text-[12px] font-inter" style={{ color: '#BA0E13' }}>{errors.max_answer}</p>}
              </div>
            </div>

            {/* Applies to */}
            <div style={{ width: '100%' }}>
              <div className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#222525', marginBottom: '4px',padding:'10px 16px',backgroundColor:'#EBF0F0',borderRadius:"12px" }}>
                {language?.['Applies to:'] ?? 'Applies to:'}
              </div>
              <div className="flex flex-col">
                <div
                  role="checkbox"
                  aria-checked={formValues.is_all}
                  tabIndex={0}
                  onKeyDown={clickOnSpace}
                  className="flex items-center justify-between cursor-pointer"
                  style={{ padding: '14px 0', borderBottom: '1px solid #EBF0F0' }}
                  onClick={() => setFormValues((prev) => ({ ...prev, is_all: !prev.is_all }))}
                >
                  <span className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                    {language?.['All'] ?? 'All'}
                  </span>
                  <div
                    className="w-5 h-5 flex items-center justify-center"
                    style={{
                      border: formValues.is_all ? '' : '1px solid #C9D7D8',
                      backgroundColor: formValues.is_all ? '#0F7679' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    {formValues.is_all ? <WhiteCheckIcon /> : null}
                  </div>
                </div>
                <div
                  role="checkbox"
                  aria-checked={formValues.is_device}
                  tabIndex={0}
                  onKeyDown={clickOnSpace}
                  className="flex items-center justify-between cursor-pointer"
                  style={{ padding: '14px 0', borderBottom: '1px solid #EBF0F0' }}
                  onClick={() => setFormValues((prev) => ({ ...prev, is_device: !prev.is_device }))}
                >
                  <span className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                    {language?.['Device only'] ?? 'Device only'}
                  </span>
                  <div
                    className="w-5 h-5 flex items-center justify-center"
                    style={{
                      border: formValues.is_device ? '' : '1px solid #C9D7D8',
                      backgroundColor: formValues.is_device ? '#0F7679' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    {formValues.is_device ? <WhiteCheckIcon /> : null}
                  </div>
                </div>
                <div
                  role="checkbox"
                  aria-checked={formValues.is_web}
                  tabIndex={0}
                  onKeyDown={clickOnSpace}
                  className="flex items-center justify-between cursor-pointer"
                  style={{ padding: '14px 0' }}
                  onClick={() => setFormValues((prev) => ({ ...prev, is_web: !prev.is_web }))}
                >
                  <span className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                    {language?.['Web'] ?? 'Web'}
                  </span>
                  <div
                    className="w-5 h-5 flex items-center justify-center"
                    style={{
                      border: formValues.is_web ? '' : '1px solid #C9D7D8',
                      backgroundColor: formValues.is_web ? '#0F7679' : 'transparent',
                      borderRadius: '6px',
                    }}
                  >
                    {formValues.is_web ? <WhiteCheckIcon /> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 2 ===== */}
        {step === 2 && (
          <div style={{ marginTop: '24px' }}>
            <p
              className="text-[16px] font-inter leading-[20px] font-medium"
              style={{ color: '#222525', marginBottom: '20px' }}
            >
              {language?.['Associate product to modifier'] ?? 'Associate product to modifier'}
            </p>

            {/* Add form row */}
            <div {...rowKb.scopeProps}>
            <div className="flex items-end" style={{ gap: '12px', marginBottom: '24px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              {formValues.modifier_type === 1 ? (
                <>
                  <div style={{ flex: '1 1 0%', minWidth: isMobile ? '100%' : 0 }}>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                          {language?.['Product'] ?? 'Product'}
                        </p>
                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                      </div>
                      <DropdownSearch<ProductOption>
                        refKey="product"
                        refs={{ product: productDropdownRef }}
                        openStates={{ product: isProductDropdownOpen }}
                        open={(key) => key === 'product' && setIsProductDropdownOpen(true)}
                        close={(key) => key === 'product' && setIsProductDropdownOpen(false)}
                        data={productOptions}
                        labelExtractor={(item) => item.sku ? `${item.sku} - ${item.productName}` : item.productName}
                        value={productDropdownLabel}
                        setValue={setProductDropdownLabel}
                        onSelect={(item) => {
                          setAddForm((f) => ({ ...f, product_id: item.id }));
                          setProductDropdownLabel(item.sku ? `${item.sku} - ${item.productName}` : item.productName);
                        }}
                        onSearch={handleStep2ProductSearch}
                        placeholder={language?.['Select product'] ?? 'Select product'}
                        borderNone={false}
                        onLoadMore={handleStep2ProductLoadMore}
                        hasMore={productOptionsHasMore}
                        loading={productOptionsLoading}
                      />
                    </div>
                  </div>
                  <div style={{ width: isMobile ? '100%' : '160px', flexShrink: 0 }}>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                          {language?.['Max answer'] ?? 'Max answer'}
                        </p>
                        <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                      </div>
                      <input
                        type="number"
                        min={0}
                        value={addForm.max_answer}
                        onChange={(e) => setAddForm((f) => ({ ...f, max_answer: Number(e.target.value) }))}
                        className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ flex: '1 1 0%', minWidth: isMobile ? '100%' : 0 }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                        {language?.['Answer'] ?? 'Answer'}
                      </p>
                      <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#BA0E13' }}>*</p>
                    </div>
                    <input
                      type="text"
                      value={addForm.answer_text}
                      onChange={(e) => setAddForm((f) => ({ ...f, answer_text: e.target.value }))}
                      placeholder={language?.['e.g. Rare, Medium Rare'] ?? 'e.g. Rare, Medium Rare'}
                      className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                    />
                  </div>
                </div>
              )}
              <div style={{ width: isMobile ? '100%' : '160px', flexShrink: 0 }}>
                <div className="space-y-2">
                  <p className="text-[14px] font-inter leading-[20px] font-medium" style={{ color: '#4A5353' }}>
                    {language?.['Display order'] ?? 'Display order'}
                  </p>
                  <input
                    type="number"
                    min={0}
                    value={addForm.display_order}
                    onChange={(e) => setAddForm((f) => ({ ...f, display_order: Number(e.target.value) }))}
                    className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
                  />
                </div>
              </div>
              <button
                type="button"
                {...rowKb.primaryProps(addAnswerFromForm)}
                style={{
                  padding: '10px 24px',
                  borderRadius: '30px',
                  color: '#0F7679',
                  fontSize: '14px',
                  fontWeight: '500',
                border: '1px solid #0F7679',
                  flexShrink: 0,
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                className="font-inter cursor-pointer"
              >
                {language?.['Add'] ?? 'Add'}
              </button>
            </div>
            </div>
            {errors.step2 && <p className="text-[12px] font-inter" style={{ color: '#BA0E13', marginBottom: '12px' }}>{errors.step2}</p>}

            {/* Added items title */}
            <p
              className="text-[16px] font-inter leading-[20px] font-medium"
              style={{ color: '#4A5353', marginBottom: '8px' }}
            >
              {language?.['Added items'] ?? 'Added items'}
            </p>

            {/* Added items table */}
            <div className="scroll-container" style={{ overflowY: 'auto' }}>
              {answers.length === 0 ? (
                <p className="text-[14px] font-inter" style={{ color: '#6A7878', padding: '20px 0' }}>
                  {language?.['No answer options added yet'] ?? 'No answer options added yet'}
                </p>
              ) : (
                answers.map((a, idx) => {
                  const itemProduct = a.product_id ? productOptions.find((p) => p.id === a.product_id) : null;
                  const itemLabel = formValues.modifier_type === 1
                    ? (itemProduct ? `${itemProduct.sku ? itemProduct.sku + ' - ' : ''}${itemProduct.productName}` : a.answer_text) || '—'
                    : a.answer_text || '—';
                  return (
                    <div
                      key={idx}
                      className="flex items-center"
                      style={{ padding: '12px 0', borderBottom: '1px solid #EBF0F0' }}
                    >
                      <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                        <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                          {language?.['Item'] ?? 'Item'}
                        </p>
                        <p className="text-[14px] font-inter font-medium" style={{ color: '#222525', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {itemLabel}
                        </p>
                      </div>
                      {formValues.modifier_type === 1 && (
                        <div style={{ width: '120px', flexShrink: 0, paddingLeft: '16px' }}>
                          <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                            {language?.['Max answer'] ?? 'Max answer'}
                          </p>
                          <p className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                            {a.max_answer ?? 1}
                          </p>
                        </div>
                      )}
                      <div style={{ width: '120px', flexShrink: 0, paddingLeft: '16px' }}>
                        <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                          {language?.['Display order'] ?? 'Display order'}
                        </p>
                        <p className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                          {a.display_order}
                        </p>
                      </div>
                      <div style={{ width: '120px', flexShrink: 0, paddingLeft: '16px' }}>
                        <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                          {language?.['Date'] ?? 'Date'}
                        </p>
                        <p className="text-[14px] font-inter font-medium" style={{ color: '#222525' }}>
                          {formatDate(a.date)}
                        </p>
                      </div>
                      <div style={{ width: '80px', flexShrink: 0, paddingLeft: '16px' }}>
                        <p className="text-[12px] font-inter" style={{ color: '#6A7878' }}>
                          {language?.['Active'] ?? 'Active'}
                        </p>
                        <div className="flex items-center gap-x-3">
                            <div
                              role="switch"
                              aria-checked={!!a.is_active}
                              aria-label={language?.['Active'] ?? 'Active'}
                              tabIndex={0}
                              onKeyDown={clickOnSpace}
                              className={`switch ${a.is_active ? 'on' : 'off'}`}
                              onClick={() => {
                                toggleAnswerActive(idx);
                              }}
                            >
                              <div
                                className={`shadow_toggle slider ${a.is_active ? 'move-right' : 'move-left'
                                  }`}
                              ></div>
                            </div>
                          </div>
                      </div>
                      <div className="flex items-center" style={{ gap: '4px', flexShrink: 0, paddingLeft: '16px' }}>
                        <button
                          type="button"
                          aria-label={`${language?.['Edit'] ?? 'Edit'} ${itemLabel}`}
                          className="cursor-pointer flex items-center justify-center"
                          onClick={() => editAnswer(idx)}
                          style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                        >
                          <EditIconV2 />
                        </button>
                        <button
                          type="button"
                          aria-label={`${language?.['Delete'] ?? 'Delete'} ${itemLabel}`}
                          className="cursor-pointer flex items-center justify-center"
                          onClick={() => removeAnswer(idx)}
                          style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                        >
                          <DeleteIconV2 />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ===== STEP 3 ===== */}
        {step === 3 && (
          <form id="create-modifier-form" onSubmit={handleSubmit} style={{ height: '100%' }}>
            {formValues.modifier_type === 1 ? (
              <>
                <div className="flex items-center justify-between" style={{ marginTop: '24px', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                  <p
                    className="text-[16px] font-inter leading-[20px] font-medium"
                    style={{ color: '#222525' }}
                  >
                    {language?.['Select products to prompt with the question'] ?? 'Select products to prompt with the question'}
                  </p>
                  <div className="flex items-center gap-x-2">
                    <div className="relative" style={{ width: isMobile ? '100%' : '400px', height: '40px' }}>
                      <input
                        ref={searchInputRef}
                        style={{
                          borderColor: '#E7F1F1',
                          padding: '8px 36px',
                          minWidth: isMobile ? '100%' : '332px',
                          borderRadius: '12px',
                          border: '1px solid #C9D7D8',
                          color: '#222525',
                        }}
                        name="searchValue"
                        onKeyDown={handleSubmitSearch}
                        value={filter.searchValue}
                        onChange={(e: any) => setFilter({ ...filter, searchValue: e.target.value })}
                        placeholder={language?.['Search product'] ?? 'Search product'}
                        className="text-[14px] font-inter w-full h-full rounded-md outline-none focus:none bg-white bg-transparent"
                      />
                      <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '8px' }}>
                        <SearchIcon />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  ref={scrollProductRef}
                  className="scroll-container"
                  style={{ overflowY: 'scroll', height: 'calc(100% - 80px)' }}
                >
                  <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F5F7F7' }}>
                        <th className="text-[14px] font-inter leading-[20px] font-medium p-3" style={{ color: '#4A5353',textAlign:'left' }}>
                          {language?.['SKU'] ?? 'SKU'}
                        </th>
                        <th className="text-[14px] font-inter leading-[20px] font-medium p-3" style={{ color: '#4A5353',textAlign:'left' }}>
                          {language?.['Group'] ?? 'Group'}
                        </th>
                        <th className="text-[14px] font-inter leading-[20px] font-medium p-3" style={{ color: '#4A5353',textAlign:'left' }}>
                          {language?.['Product'] ?? 'Product'}
                        </th>
                        <th className="text-[14px] font-inter leading-[20px] font-medium p-3" style={{ color: '#4A5353', width: '80px' }}>
                          {language?.['Actions'] ?? 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {listProducts.map((value) => (
                        <tr key={value.id} style={{ borderTop: '1px solid #EBF0F0' }}>
                          <td className="text-[14px] font-inter p-3" style={{ color: '#222525' }}>
                            {value.sku ?? '—'}
                          </td>
                          <td className="text-[14px] font-inter p-3" style={{ color: '#222525' }}>
                            {value.productGroup?.groupName ?? '—'}
                          </td>
                          <td className="text-[14px] font-inter p-3" style={{ color: '#222525' }}>
                            {value.productName}
                          </td>
                          <td className="p-3 flex" style={{ justifyItems:'end' }}>
                            <div
                              role="checkbox"
                              aria-checked={!!value.isAttributes}
                              aria-label={value.productName}
                              tabIndex={0}
                              onKeyDown={clickOnSpace}
                              className="cursor-pointer w-5 h-5 flex items-center justify-center"
                              onClick={() => handleSelectProduct(value)}
                              style={{
                                border: value.isAttributes ? '' : '1px solid #C9D7D8',
                                backgroundColor: value.isAttributes ? '#0F7679' : 'transparent',
                                borderRadius: '6px',
                              }}
                            >
                              {value.isAttributes ? <WhiteCheckIcon /> : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div ref={setLoaderRef} style={{ height: '1px' }}>
                    {loadingProduct && (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="loader"></span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div
                style={{
                  marginTop: '24px',
                  padding: '24px',
                  backgroundColor: '#F5F7F7',
                  borderRadius: '12px',
                  border: '1px solid #EBF0F0',
                }}
              >
                <p className="text-[14px] font-inter" style={{ color: '#4A5353' }}>
                  {language?.['This modifier type does not require product assignment.'] ?? 'This modifier type does not require product assignment.'}
                </p>
              </div>
            )}
          </form>
        )}
      </div>

      {/* Footer */}
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: '12px', paddingTop: '24px' }}
      >
        <div
          className="absolute"
          style={{
            left: '-24px',
            top: 0,
            height: '1px',
            backgroundColor: '#DBE4E4',
            width: isMobile ? 'calc(100vw + 32px)' : 'calc(-256px + 48px + 100vw)',
          }}
        />
        <button
          type="button"
          {...kb.stepProps(handleBackNavigation)}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {step === 1
            ? (language?.['Cancel'] ?? 'Cancel')
            : (language?.['Back'] ?? 'Back')}
        </button>
        {step < 3 ? (
          <button
            type="button"
            {...kb.primaryProps(() => {
              step === 1 ? handleStep1Next() : handleStep2Next();
            })}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language?.['Next'] ?? 'Next'}
          </button>
        ) : (
          <button
            type="button"
            {...kb.primaryProps(handleSubmit)}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language?.['Save'] ?? 'Save'}
          </button>
        )}
      </div>
    </div>
    </div>
  );
};

export default CreateProductModifier;
